import type {CharacterSenses, SpecialSense} from "@/characters/model/CharacterSenses.ts";
import {NEAR_SIGHT_RADIUS_METERS, TOKEN_CONE_MIN_COSINE} from "./SightModel.ts";
import {DEFAULT_SIGHT_RADIUS_METERS} from "./SightRange.ts";

export type PerceptionProfile = {
    normalRangeMeters: number;
    darkvisionRangeMeters: number;
    blindsightRangeMeters: number;
    truesightRangeMeters: number;
    /** Stored for future ground-contact rules; currently grants no perception. */
    tremorsenseRangeMeters: number;
    nearRangeMeters: number;
    geometryRangeMeters: number;
};

/** Static character capabilities only. Combatant conditions are deliberately not evaluated yet. */
export function perceptionProfile(senses: CharacterSenses): PerceptionProfile {
    const range = (type: SpecialSense["type"], predicate: (sense: SpecialSense) => boolean = () => true) =>
        Math.max(0, ...senses.specialSenses.filter(sense => sense.type === type && predicate(sense)
            && Number.isFinite(sense.rangeMeters) && sense.rangeMeters > 0).map(sense => sense.rangeMeters));
    const blindLimit = range("BLINDSIGHT", sense => sense.blindOutsideRange);
    const visualRange = (meters: number) => blindLimit > 0 ? Math.min(meters, blindLimit) : meters;
    const normalRangeMeters = visualRange(DEFAULT_SIGHT_RADIUS_METERS);
    const darkvisionRangeMeters = visualRange(range("DARKVISION"));
    const blindsightRangeMeters = range("BLINDSIGHT");
    const truesightRangeMeters = visualRange(range("TRUESIGHT"));
    return {normalRangeMeters, darkvisionRangeMeters, blindsightRangeMeters, truesightRangeMeters,
        tremorsenseRangeMeters: range("TREMORSENSE"), nearRangeMeters: NEAR_SIGHT_RADIUS_METERS,
        geometryRangeMeters: Math.max(normalRangeMeters, darkvisionRangeMeters, blindsightRangeMeters,
            truesightRangeMeters, NEAR_SIGHT_RADIUS_METERS)};
}

export type PerceptionQuery = {
    distanceMeters: number;
    directionCosine: number;
    lineOfSight: boolean;
    /** The common illumination model supplies this decision; no display-brightness threshold here. */
    illuminated: boolean;
};

export function visuallyPerceives(profile: PerceptionProfile, query: PerceptionQuery): boolean {
    return query.lineOfSight && query.distanceMeters >= 0 && Number.isFinite(query.distanceMeters)
        && query.directionCosine >= TOKEN_CONE_MIN_COSINE - 1e-7
        && (query.distanceMeters <= profile.darkvisionRangeMeters && profile.darkvisionRangeMeters > 0
            || query.illuminated && query.distanceMeters <= profile.normalRangeMeters);
}

/** Shared rules for later token/hostile-sight integration; hidden flags are checked by the caller. */
export function perceivesToken(profile: PerceptionProfile, query: PerceptionQuery): boolean {
    if (!query.lineOfSight || query.distanceMeters < 0 || !Number.isFinite(query.distanceMeters)) return false;
    return query.distanceMeters <= profile.nearRangeMeters
        || profile.blindsightRangeMeters > 0 && query.distanceMeters <= profile.blindsightRangeMeters
        || profile.truesightRangeMeters > 0 && query.distanceMeters <= profile.truesightRangeMeters
            && query.directionCosine >= TOKEN_CONE_MIN_COSINE - 1e-7
        || visuallyPerceives(profile, query);
}
